import {
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Box, HStack, Stack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {SchemaOf, mixed, object, string} from 'yup';

import {useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';

import {isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {omneticApi} from '@dms/api/base';
import {selectTenant, useGetParticipationQuery} from '@dms/api/core';
import {OfferSaleVehiclePatchRequestBody, OfferSaleVehicleResponseBody} from '@dms/api/finance';
import {useGetBusinessCaseQuery, usePatchOfferSaleVehicleMutation} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DEFAULT_CURRENCY,
  NoPermissionTooltip,
  handleApiError,
  usePermissions,
  useSaleVehicleActions,
} from '@dms/shared';
import {$MoneyAmount} from '@dms/teas';

import {ApiError, isNotNilOrEmpty, useRequiredParams} from 'shared/web';

const invoiceOrFurtherStatuses = ['INVOICE', 'PAID', 'CLOSED', 'UNSUCCESSFUL'] as const;

// eslint-disable-next-line complexity -- Pre-existing violation
export const BusinessCaseSaleVehicleDiscountForm = () => {
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const dispatch = useDispatch();
  const [lastTouched, setLastTouched] = useState<
    'discountPercentage' | 'discountPrice' | undefined
  >(undefined);
  const formApiRef = useRef<UseFormReturn<OfferSaleVehiclePatchRequestBody> | null>(null);

  const {id: businessCaseId} = useRequiredParams();

  const [patchSaleVehicle, {isLoading: isPatchingSaleVehicle}] = usePatchOfferSaleVehicleMutation();

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});

  const [canEditDiscount] = usePermissions({
    permissionKeys: ['businessCaseEditSaleDiscount'],
    scopes: {
      businessCaseEditSaleDiscount: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const {data: {currency: tenantCurrency} = {}} = useSelector(selectTenant);

  const offer = businessCase?.offers[0];
  const saleVehicles = businessCase?.offers?.[0]?.saleVehicles;

  const saleVehicle = saleVehicles?.[0] as OfferSaleVehicleResponseBody;
  const defaultValues: OfferSaleVehiclePatchRequestBody = {
    discountPrice:
      saleVehicle?.discountPrice?.withVat?.amount ??
      saleVehicle?.discountPrice?.withoutVat?.amount ??
      null,
    discountPercentage: saleVehicle?.discountPercentage ?? null,
    discountReason: saleVehicle?.discountReason ?? null,
  };

  const {isSaleActionEnabled} = useSaleVehicleActions(saleVehicle?.vehicleId ?? '');

  const defaultCurrency = isCurrency(tenantCurrency) ? tenantCurrency : DEFAULT_CURRENCY;
  const currency = isCurrency(saleVehicle?.discountPrice?.withVat?.currency)
    ? saleVehicle?.discountPrice?.withVat?.currency
    : defaultCurrency;

  const sellingPriceAmount = Number(saleVehicle?.sellingPrice?.withVat?.amount);

  const handleDiscountPercentageChange = (value: number | null) => {
    setLastTouched('discountPercentage');
    if (isNaN(sellingPriceAmount) || sellingPriceAmount === 0) {
      return;
    }
    formApiRef.current?.clearErrors('discountPrice');
    formApiRef.current?.setValue(
      'discountPrice',
      isNil(value) ? null : ((sellingPriceAmount * value) / 100).toFixed(2)
    );
  };

  const handleDiscountPriceChange = (value: number | null) => {
    setLastTouched('discountPrice');
    if (isNaN(sellingPriceAmount) || sellingPriceAmount === 0) {
      return;
    }
    formApiRef.current?.clearErrors('discountPercentage');
    formApiRef.current?.setValue(
      'discountPercentage',
      isNil(value) ? null : ((value / sellingPriceAmount) * 100).toFixed(2)
    );
  };

  const handleSubmit: FormSubmitHandler<OfferSaleVehiclePatchRequestBody> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    if (isNilOrEmpty(saleVehicle)) {
      throw new Error('Sale vehicle not found');
    }
    if (isNilOrEmpty(offer?.id)) {
      return;
    }
    const discountReason = values.discountReason || null;
    const discountPercentage = values.discountPercentage?.toString() || null;
    const discountPrice = values.discountPrice?.toString() || null;

    const lastTouchedPercentage = lastTouched === 'discountPercentage';

    const offerSaleVehiclePatchRequestBody = {
      discountPercentage: lastTouchedPercentage ? discountPercentage : null,
      discountPrice: !lastTouchedPercentage ? discountPrice : null,
      discountReason,
    };

    await patchSaleVehicle({
      offerId: offer!.id,
      offerSaleVehiclePatchRequestBody,
      businessCaseId,
      offerSaleVehicleId: saleVehicle?.id,
    })
      .unwrap()
      .then((data) => {
        if (!formApiRef.current) {
          return;
        }

        if (
          lastTouched === 'discountPrice' &&
          isNotNilOrEmpty(data.saleVehicles?.[0]?.discountPercentage)
        ) {
          formApiRef.current.setValue(
            'discountPercentage',
            data.saleVehicles[0].discountPercentage
          );
        }
        if (
          lastTouched === 'discountPercentage' &&
          (isNotNilOrEmpty(data.saleVehicles?.[0]?.discountPrice?.withVat?.amount) ||
            isNotNilOrEmpty(data.saleVehicles?.[0]?.discountPrice?.withoutVat?.amount))
        ) {
          formApiRef.current.setValue(
            'discountPrice',
            data.saleVehicles[0].discountPrice.withVat?.amount ??
              data.saleVehicles[0].discountPrice.withoutVat?.amount ??
              null
          );
        }

        if (lastTouched === 'discountPrice' && isNilOrEmpty(values.discountPrice)) {
          formApiRef.current.setValue('discountPercentage', null);
        }

        if (lastTouched === 'discountPercentage' && isNilOrEmpty(values.discountPercentage)) {
          formApiRef.current.setValue('discountPrice', null);
        }

        if (businessCase?.checkoutId) {
          dispatch(omneticApi.util.invalidateTags([{type: 'offer', id: businessCase.checkoutId}]));
        }

        reset(formApiRef.current.getValues());
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const isInvoiceOrFurtherStatus = isNotNilOrEmpty(businessCase?.businessCaseState)
    ? invoiceOrFurtherStatuses.includes(businessCase!.businessCaseState)
    : false;

  const isFieldDisabled =
    !isSaleActionEnabled('SALE_VEHICLE_UPDATE') || not(canEditDiscount) || isInvoiceOrFurtherStatus;
  if (isNilOrEmpty(saleVehicle)) {
    return null;
  }

  return (
    <Form<OfferSaleVehiclePatchRequestBody>
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      schema={schema(saleVehicle?.sellingPrice?.withVat?.amount)}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => {
        formApiRef.current = formApi;

        return (
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.businessCase.labels.customerDiscount')}
            data-testid={testIds.businessCase.selling('discount')}
          >
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={!canEditDiscount}>
                  <FormField
                    type="number"
                    name="discountPercentage"
                    control={control}
                    label={i18n.t('page.businessCase.labels.percentage')}
                    suffix="%"
                    isDisabled={isFieldDisabled}
                    onChange={handleDiscountPercentageChange}
                    data-testid={testIds.businessCase.selling('discountPercentage')}
                  />
                </NoPermissionTooltip>
              </Box>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={!canEditDiscount}>
                  <FormField
                    type="number"
                    name="discountPrice"
                    control={control}
                    label={i18n.t('general.labels.amount')}
                    suffix={formatCurrencySymbol(currency)}
                    isDisabled={isFieldDisabled}
                    onChange={handleDiscountPriceChange}
                    data-testid={testIds.businessCase.selling('discountPrice')}
                  />
                </NoPermissionTooltip>
              </Box>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={!canEditDiscount}>
                  <FormField
                    type="text"
                    name="discountReason"
                    control={control}
                    label={i18n.t('general.labels.reason')}
                    isDisabled={isFieldDisabled}
                    data-testid={testIds.businessCase.selling('discountReason')}
                  />
                </NoPermissionTooltip>
              </Box>
              <NoPermissionTooltip shouldShowTooltip={!canEditDiscount}>
                <HStack justify="flex-end" align="flex-end">
                  <FormButton
                    type="submit"
                    control={control}
                    title={i18n.t('general.actions.save')}
                    isDisabled={isFieldDisabled || !formApi.formState.isDirty}
                    isLoading={isPatchingSaleVehicle}
                    data-testid={testIds.businessCase.selling('discountSubmitButton')}
                  />
                </HStack>
              </NoPermissionTooltip>
            </Stack>
          </Card>
        );
      }}
    </Form>
  );
};

const schema = (sellingPrice?: string): SchemaOf<OfferSaleVehiclePatchRequestBody> =>
  object().shape({
    discountPrice: $MoneyAmount({max: {amount: sellingPrice}})
      .defined()
      .nullable(),
    discountPercentage: mixed()
      .defined()
      .test(
        'max',
        i18n.t('general.notifications.numberMaxValidationMessage'),
        (value) => !value || value <= 100
      )
      .test(
        'typeError',
        i18n.t('general.notifications.numberValidationMessage'),
        (value) => !value || !isNaN(value)
      )
      .test(
        'positive',
        i18n.t('general.notifications.numberPositive'),
        (value) => !value || value >= 0
      )
      .nullable(),
    discountReason: string().defined().nullable(),
  });
